import { fetchInsuranceProducts } from 'actions/InsuranceActions'
import getCheckoutInsuranceCoverAmount from 'checkout/selectors/request/getCheckoutInsuranceCoverAmount'
import GeoContext from 'contexts/geoContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { nonNullable } from 'lib/array/arrayUtils'
import { getInsuranceProductListKey } from 'lib/insurance/insuranceUtils'
import { useContext, useEffect, useMemo } from 'react'

interface InsuranceProductsOptions {
  isCruise?: boolean;
  isDomestic?: boolean;
  subscriptionId?: string;
  disabled?: boolean;
}

type ProductsReturn = [
  products: Array<App.InsuranceProduct>,
  fetching: boolean,
  error: any,
]

function useInsuranceProducts(
  type: App.InsuranceProductType,
  options: InsuranceProductsOptions = {},
): ProductsReturn {
  const { currentCurrency } = useContext(GeoContext)

  const dispatch = useAppDispatch()

  const cartCoverAmount = useAppSelector(state => getCheckoutInsuranceCoverAmount(state))
  const productListKey = getInsuranceProductListKey(type, { ...options, coverAmount: cartCoverAmount })
  const productList = useAppSelector(state => state.insurance.productLists[productListKey])
  const allProducts = useAppSelector(state => state.insurance.products)
  const fetching = useAppSelector(state => !!state.insurance.fetchingProductLists[productListKey])
  const error = useAppSelector(state => state.insurance.productListErrors[productListKey])

  useEffect(() => {
    if (!options.disabled) {
      dispatch(fetchInsuranceProducts(type, {
        isCruise: options.isCruise,
        isDomestic: options.isDomestic,
        subscriptionId: options.subscriptionId,
        coverAmount: cartCoverAmount,
        currency: currentCurrency,
      }))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productListKey, options.disabled, cartCoverAmount])

  const products = useMemo(() => {
    const ids = productList ?? []
    return nonNullable(ids.map(id => allProducts[id]))
  }, [allProducts, productList])

  return [
    products,
    fetching,
    error,
  ]
}

export default useInsuranceProducts
